import { Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import DashboardCard from '/@/components/bayer/dashboard-card';
import BayerTabs from '/@/components/bayer/tabs';
import useUnitProvider from '/@/core/context/unit';
import { useGrowersDashboardReports } from '/@/hooks/dashboards';
import { _ } from '/@/locale';
import { getNodeScopeFilters } from '/@/utils/node-scope';

const GrowersRankingReportDashboardCard = ({ year }: { year: number }) => {
    const [currentGrowerZone, setCurrentGrowerZone] = useState<string | undefined>(undefined);
    const units = useUnitProvider();

    const { data: dashboardReports, isValidating } = useGrowersDashboardReports({
        year,
        ...getNodeScopeFilters('rvs'),
    });

    type DashboardReports = NonNullable<typeof dashboardReports>;

    const parsedYieldByGrowers = dashboardReports?.harvest?.avgYieldByGrowers?.reduce(
        (acc, grower) => {
            const zone = _(grower.zoneShortName);
            if (!acc[zone]) {
                acc[zone] = [];
            }
            acc[zone].push(grower);
            return acc;
        },
        {} as Record<string, DashboardReports['harvest']['avgYieldByGrowers']>
    );
    useEffect(() => {
        // Set initial zones if not set
        if (!currentGrowerZone && dashboardReports.avaiableZones?.length > 0) {
            setCurrentGrowerZone(dashboardReports.avaiableZones[0]!.value);
        }
    }, [currentGrowerZone, dashboardReports.avaiableZones]);

    return (
        <DashboardCard
            title={_('Ranking de productores')}
            isLoading={isValidating}
            subtitle={_('Mejor rendimiento promedio de maíz.')}
            spacing={0.5}
        >
            <Stack direction='column' spacing={2} sx={{ height: 'fill-available', pt: 1.5 }}>
                <BayerTabs
                    value={currentGrowerZone}
                    setValue={setCurrentGrowerZone}
                    options={
                        dashboardReports.zones?.map((zone) => ({
                            label: zone.value as string,
                            value: zone.value as string,
                            disabled: zone.isDisabled,
                            tooltipText: zone.isDisabled ? _('No hay información para esta zona') : '',
                        })) || []
                    }
                    variant='chips'
                />
                <Stack direction='column' spacing={0.5}>
                    {parsedYieldByGrowers?.[currentGrowerZone]?.map(
                        (growerYield: DashboardReports['harvest']['avgYieldByGrowers'][number]) => (
                            <Stack
                                key={growerYield.growerId}
                                direction='row'
                                spacing={2}
                                justifyContent='space-between'
                                alignItems='start'
                            >
                                <Stack direction='column' spacing={0.5}>
                                    <Typography variant='subtitle1' color='primary'>
                                        {growerYield.grower?.isSelf
                                            ? _('Mi cuenta')
                                            : `${_('Productor')} ${growerYield.grower?.name}`}
                                    </Typography>
                                    {/* FIXME: `parent` is deprecated, use `parents?.[0]` instead */}
                                    <Typography variant='caption' color='primary.mui.inactive'>
                                        {growerYield.grower?.parent}
                                    </Typography>
                                </Stack>
                                <Typography variant='subtitle1' fontWeight={700} color='primary'>
                                    {units.toYield(growerYield.avgYield, 'corn', 0).toString()}
                                </Typography>
                            </Stack>
                        )
                    )}
                </Stack>
            </Stack>
        </DashboardCard>
    );
};

export default GrowersRankingReportDashboardCard;
